import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1PlatformSchema } from './vocabulary';
/**
 * Public inventory-user contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * **What an inventory user is.** A developer the Kirin agent observes — not a
 * login. A row here may have no Kirin account at all, which is why this resource
 * is `inventory-users` and the account-holders resource (slice 4) is `accounts`.
 * The two are deliberately separate names for deliberately different things; see
 * the vocabulary table in `NAMING.md`.
 *
 * **Gated to manager, owner or support**, copied from the UI route. Every row is
 * a named person's activity, so this is the most PII-dense resource on the
 * surface and a member-minted key must not read it.
 *
 * **Two internal fields are deliberately withheld.** The internal row can carry
 * the account a developer is signed in to a tool with (`loggedInUserEmail`) and
 * how that account matched their Kirin identity — both populated only while the
 * `shadow_ai_logged_in_user` flag is on. A published field that is present or
 * absent depending on a flag is a field callers cannot rely on, and `v1` freezes
 * what it publishes for the life of the version. Publishing them is a decision
 * to take once, deliberately, rather than by proximity.
 */
/** How much of a platform Kirin can see. */
export const v1CoverageSchema = z
    .enum(['covered', 'supported', 'unknown'])
    .meta({
    id: 'V1Coverage',
    title: 'Platform coverage',
    description: 'How much Kirin can see of a platform: `covered` — Kirin is installed and actively monitoring; `supported` — Kirin supports the tool but is not collecting from it yet; `unknown` — Kirin does not support the tool.',
});
/** Alert severity, highest-first vocabulary shared with the alerts resource. */
export const v1InventoryUserAlertSeveritySchema = z
    .enum(['low', 'medium', 'high', 'critical'])
    .meta({
    id: 'V1InventoryUserAlertSeverity',
    title: 'Alert severity',
    description: "The severity of an alert raised by an endpoint user's activity, highest-first: `critical`, `high`, `medium`, `low`.",
});
/**
 * One platform a developer was seen on, with what Kirin can see of it.
 *
 * Extends the shared {@link v1PlatformSchema}, which is what makes `name`
 * non-null here. It used to be nullable and was null in the **common** case: the
 * `covered_ides` branch of the posture query selects `NULL::text AS
 * display_name`, so every actively-monitored platform published `name: null`
 * while the screen rendered "Cursor" or "Claude Code" from the registry. The
 * uncommon case was worse than null — a Shadow-AI-derived row carried that
 * source's looser name ("Claude"), which the app deliberately discards in favour
 * of the registry label. The DTO now resolves the label the same way the app
 * does; see `resolvePlatformName`.
 */
export const v1InventoryUserPlatformSchema = v1PlatformSchema
    .extend({
    coverage: v1CoverageSchema,
    coverageName: z
        .string()
        .describe('The coverage state as the Kirin app labels it — "Covered", "Supported (No Coverage)" or "Unknown". Published alongside the key because "Supported (No Coverage)" is not derivable from `supported`.'),
    lastActiveAt: z
        .string()
        .datetime()
        .nullable()
        .describe('The most recent activity Kirin saw on this platform, ISO 8601 UTC. Null for a platform Kirin knows about but has no activity for — every `supported` and `unknown` platform, and a `covered` one that has not reported yet.'),
})
    .meta({
    id: 'V1InventoryUserPlatform',
    title: 'Inventory user platform',
    description: 'One IDE or coding agent an endpoint user was seen on, and how much of it Kirin can see.',
    examples: [
        {
            id: 'claude',
            name: 'Claude Code',
            coverage: 'covered',
            coverageName: 'Covered',
            lastActiveAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** How many MCP servers a developer has on one platform. */
export const v1InventoryUserMcpByPlatformSchema = z
    .object({
    platform: v1PlatformSchema.describe('The platform, with the display name the Kirin app shows. An object rather than a bare key, matching `platforms[]` on the same row — the two used to disagree, one carrying a label and the other not.'),
    mcpServerCount: z
        .number()
        .int()
        .describe('How many MCP servers this endpoint user has on that platform.'),
})
    .meta({
    id: 'V1InventoryUserMcpByPlatform',
    title: 'Inventory user MCP breakdown',
    description: "How many MCP servers one endpoint user has on one platform. Which servers those are is on the MCP-server resource's `users` sub-resource.",
    examples: [
        {
            platform: { id: 'cursor', name: 'Cursor' },
            mcpServerCount: 4,
        },
    ],
});
/** One endpoint user's security posture. */
export const v1InventoryUserSchema = z
    .object({
    id: z
        .string()
        .describe("The endpoint user's identifier within your organization. The same value the other inventory resources' `users` sub-resources return, so it is the join key across this API."),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    platforms: z
        .array(v1InventoryUserPlatformSchema)
        .describe('Every IDE and coding agent this endpoint user was seen on, most recently active first, each with how much of it Kirin can see.'),
    mcpServerCount: z
        .number()
        .int()
        .describe('How many MCP servers this endpoint user has, across platforms.'),
    mcpServersByPlatform: z
        .array(v1InventoryUserMcpByPlatformSchema)
        .describe('The same total, broken down by platform. Counts only — to learn *which* servers an endpoint user has, read the MCP-server resource and join on its `users` sub-resource. Naming them here would put every server of every endpoint user on every page of this list.'),
    alertCount: z
        .number()
        .int()
        .describe('How many alerts this endpoint user raised **in the last 7 days**. A rolling window, not a lifetime total: the same endpoint user read a week later can report a lower number without anything having been resolved. For any other window, query the alerts resource.'),
    maxAlertSeverity: v1InventoryUserAlertSeveritySchema
        .nullable()
        .describe('The highest severity among those last-7-day alerts, or null when there were none.'),
    lastActiveAt: z
        .string()
        .datetime()
        .nullable()
        .describe("The endpoint user's most recent activity across every platform, ISO 8601 UTC. Null for an endpoint user Kirin knows of but has never seen active."),
    endpointCount: z
        .number()
        .int()
        .nullable()
        .describe('How many distinct machines this endpoint user has reported from. **Null means not collected**, not zero — the count is only gathered while endpoint identification is enabled for your organization.'),
    oldestAgentVersion: z
        .string()
        .nullable()
        .describe("The agent version of this endpoint user's least up-to-date live machine — one seen inside your inventory-visibility window. Null when they have no live machine reporting a parseable version, and also when endpoint identification is not enabled. Use it to find who is running a stale agent."),
})
    .meta({
    id: 'V1InventoryUser',
    title: 'Inventory user',
    description: 'One endpoint user the Kirin agent observes, with what Kirin can see of their tooling and what it found there. Not a login — a row here may have no Kirin account.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            platforms: [
                {
                    id: 'claude',
                    name: 'Claude Code',
                    coverage: 'covered',
                    coverageName: 'Covered',
                    lastActiveAt: '2026-08-31T14:03:07Z',
                },
                {
                    id: 'cursor',
                    name: 'Cursor',
                    coverage: 'supported',
                    coverageName: 'Supported (No Coverage)',
                    lastActiveAt: null,
                },
            ],
            mcpServerCount: 7,
            mcpServersByPlatform: [
                {
                    platform: { id: 'claude', name: 'Claude Code' },
                    mcpServerCount: 3,
                },
                { platform: { id: 'cursor', name: 'Cursor' }, mcpServerCount: 4 },
            ],
            alertCount: 2,
            maxAlertSeverity: 'critical',
            lastActiveAt: '2026-08-31T14:03:07Z',
            endpointCount: 2,
            oldestAgentVersion: '3.77.1',
        },
    ],
});
/** Sortable fields, named for the public field they sort. */
export const v1InventoryUserSortFieldSchema = z
    .enum([
    'name',
    'email',
    'coveredPlatformCount',
    'supportedPlatformCount',
    'mcpServerCount',
    'alertCount',
    'lastActiveAt',
    'endpointCount',
    'oldestAgentVersion',
])
    .describe('Which field to sort by.');
/**
 * The list query.
 *
 * **The numeric and date filters the screen offers are not here.** Internally
 * `mcpServerCount`, `alertCount` and `lastActiveAt` are filterable with range
 * and emptiness operators — `between`, `greaterThan`, `isEmpty`. This surface's
 * filters are comma-separated exact-match values, which cannot express those,
 * and inventing an operator syntax for one resource would fork the query style
 * across the API. Sort on those fields and page instead; a later minor version
 * can add a range syntax across every resource at once.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1InventoryUserListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by display name.'),
    email: csvParam('Filter by email address.'),
    coveredPlatform: csvParam('Filter to users Kirin actively monitors on one of these platforms. Use the platform `id` from a row.'),
    supportedPlatform: csvParam('Filter to users seen on one of these platforms that Kirin supports but is not yet collecting from.'),
    endpoint: csvParam('Filter to users who have reported from a machine with one of these names or machine IDs (exact, case-insensitive).'),
    oldestAgentVersion: csvParam('Filter by the least live-machine agent version. Omits users with no parseable version (dashboard N/A).'),
    sortBy: v1InventoryUserSortFieldSchema.default('alertCount'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1InventoryUserListResponseSchema = z
    .object({
    data: z
        .array(v1InventoryUserSchema)
        .describe('This page of endpoint users.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1InventoryUserList',
    title: 'Inventory user list',
    description: 'A page of the endpoint users the Kirin agent observes in your organization.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    platforms: [
                        {
                            id: 'claude',
                            name: 'Claude Code',
                            coverage: 'covered',
                            coverageName: 'Covered',
                            lastActiveAt: '2026-08-31T14:03:07Z',
                        },
                        {
                            id: 'cursor',
                            name: 'Cursor',
                            coverage: 'supported',
                            coverageName: 'Supported (No Coverage)',
                            lastActiveAt: null,
                        },
                    ],
                    mcpServerCount: 7,
                    mcpServersByPlatform: [
                        {
                            platform: { id: 'claude', name: 'Claude Code' },
                            mcpServerCount: 3,
                        },
                        { platform: { id: 'cursor', name: 'Cursor' }, mcpServerCount: 4 },
                    ],
                    alertCount: 2,
                    maxAlertSeverity: 'critical',
                    lastActiveAt: '2026-08-31T14:03:07Z',
                    endpointCount: 2,
                    oldestAgentVersion: '3.77.1',
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 84,
                totalPages: 4,
                hasMore: true,
            },
        },
    ],
});
//# sourceMappingURL=inventory-users.js.map